import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { TextField, FontAwesomeIcon, LoaderCircle } from '@vr/lib';
import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from '@reach/combobox';
import theme from '@vr/lib/styles/theme';
import convertGoogleResponseToAddress from '../helpers/convertGoogleResponseToAddress';

import '@reach/combobox/styles.css';

const TextFieldContainer = styled.div`
  position: relative;
  .search-icon {
    position: absolute;
    top: 25px;
    right: 0;
  }
`;

const StyledComboboxPopover = styled(ComboboxPopover)`
  position: absolute;
  top: 55px;
  left: 0;
  right: 0;
  z-index: 2000;
  font-family: ${theme.fonts.family.montserrat};
  color: ${theme.colors.gray400};
  font-size: 13px;
  font-weight: 300;
  [data-reach-combobox-option] {
    border-bottom: 1px solid ${theme.colors.gray100};
    border-top: 0;
    padding: 10px;
  }
  [data-user-value] {
    font-weight: bold;
  }
  [data-suggested-value] {
    font-weight: 300;
  }
`;

const StyledTextField = styled(TextField)`
  margin-bottom: 0;
  label {
    color: ${theme.colors.gray100};
    font-weight: normal !important;
  }
  .fa-check {
    display: none;
  }
`;

const autocompleteRequestConfig = {
  componentRestrictions: {
    country: 'br',
  },
};

const TextFieldGoogleAutocomplete = React.forwardRef(
  (
    {
      google,
      label,
      messageError,
      name,
      debounce,
      onSelect,
      onChange,
      enablePlaceDetails,
      ...rest
    },
    ref,
  ) => {
    const [autocompleteService, setAutocompleteService] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const debounced = useDebouncedCallback(value => {
      if (!autocompleteService) {
        return;
      }

      setIsLoading(true);
      setSuggestions([]);
      const request = {
        input: value,
        ...autocompleteRequestConfig,
      };
      autocompleteService.getPlacePredictions(
        request,
        (predictions, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK) {
            setSuggestions([]);
          } else {
            setSuggestions(predictions);
          }
          setShowSuggestions(true);
          setIsLoading(false);
        },
      );
    }, debounce);

    function initAutocomplete() {
      if (!google) {
        setTimeout(initAutocomplete, 100);
        return;
      }

      const obj = new google.maps.places.AutocompleteService();
      setAutocompleteService(obj);
    }

    useEffect(initAutocomplete, []);

    const handleInputChange = useCallback(
      e => {
        const text = e.target.value;

        if (String(text).trim().length >= 3) {
          debounced.callback(text);
        }

        if (onChange) {
          onChange(e);
        }
      },
      [onChange, debounced],
    );

    const getPlaceDetails = useCallback(
      placeId => {
        return new Promise((resolve, reject) => {
          try {
            if (!window.mapa) {
              const element = document.createElement('div');
              window.mapa = new google.maps.Map(element);
            }

            const placesService = new google.maps.places.PlacesService(
              window.mapa,
            );

            const request = {
              placeId,
              fields: [
                'address_components',
                'formatted_phone_number',
                'geometry',
              ],
            };

            placesService.getDetails(request, (response, status) => {
              if (status === google.maps.places.PlacesServiceStatus.OK) {
                resolve(response);
              } else {
                reject(status);
              }
            });
          } catch (e) {
            reject(e.message);
          }
        });
      },
      [google],
    );

    const handleOnSelect = useCallback(
      async placeName => {
        const place = suggestions.find(item => item.description === placeName);
        const resultado = {
          place,
          location: null,
          endereco: null,
          status: false,
        };
        setShowSuggestions(false);
        try {
          const location = {};
          let endereco = null;
          if (enablePlaceDetails) {
            const response = await getPlaceDetails(place.place_id);

            location.lat = response.geometry.location.lat();
            location.lng = response.geometry.location.lng();

            endereco = convertGoogleResponseToAddress(place, response);
          } else {
            const [response] = await geocodeByPlaceId(place.place_id);
            location.lat = response.geometry.location.lat();
            location.lng = response.geometry.location.lng();
          }

          if (onSelect) {
            onSelect({ ...resultado, location, endereco, status: true });
          }
        } catch (e) {
          if (onSelect) {
            onSelect(resultado);
          }
        }
      },
      [suggestions, onSelect, enablePlaceDetails, getPlaceDetails],
    );

    const renderSuggestions = useCallback(() => {
      if (isLoading) {
        return <LoaderCircle />;
      }

      if (!isLoading && showSuggestions) {
        if (suggestions.length > 0) {
          return (
            <ComboboxList aria-labelledby="suggestions">
              {suggestions.map(item => (
                <ComboboxOption key={item.place_id} value={item.description} />
              ))}
            </ComboboxList>
          );
        }
      }

      return null;
    }, [suggestions, isLoading, showSuggestions]);

    return (
      <TextFieldContainer>
        <Combobox onSelect={handleOnSelect} openOnFocus>
          <ComboboxInput
            as={StyledTextField}
            aria-labelledby="suggestions"
            variant="light"
            ref={ref}
            name={name}
            label={label}
            messageError={messageError}
            onChange={handleInputChange}
            {...rest}
          />
          <StyledComboboxPopover portal={false}>
            {renderSuggestions()}
          </StyledComboboxPopover>
        </Combobox>
        {!messageError && (
          <FontAwesomeIcon className="search-icon" icon="search" color="#fff" />
        )}
      </TextFieldContainer>
    );
  },
);

TextFieldGoogleAutocomplete.propTypes = {
  google: PropTypes.shape({
    maps: PropTypes.object.isRequired,
  }).isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  messageError: PropTypes.string,
  onSelect: PropTypes.func,
  debounce: PropTypes.number,
  onChange: PropTypes.func,
  enablePlaceDetails: PropTypes.bool,
};

TextFieldGoogleAutocomplete.defaultProps = {
  messageError: null,
  onSelect: null,
  onChange: null,
  debounce: 500,
  enablePlaceDetails: false,
};

export default TextFieldGoogleAutocomplete;
